import { Box, SelectChangeEvent, Typography, Button } from '@mui/material';
import { NFTFilter } from './NFTFilter';
import { SecureNFTCards } from './SecureNFTCards';
import {
  DeckType,
  NftsWithUserOwnedType,
  UserOwnedNftCardType
} from '../../../../types/types';
import TotalNFTCount from './TotalNFTCount';

type ShowNftProps = {
  // deckCost: number;
  // setDeckCost: (value: number) => void;
  // maxDeckCost: number;
  createEditDeck: DeckType | null;
  setCreateEditDeck: (value: DeckType | null) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (value: boolean) => void;
  rarityFilters: string[];
  handleRarityChange: (event: SelectChangeEvent<string[]>) => void;
  typeFilters: string[];
  handleTypeChange: (event: SelectChangeEvent<string[]>) => void;
  factionFilters: string[];
  handleFactionChange: (event: SelectChangeEvent<string[]>) => void;
  setAttackFromFilter: (value: number | null) => void;
  setAttackToFilter: (value: number | null) => void;
  setDefenceFromFilter: (value: number | null) => void;
  setDefenceToFilter: (value: number | null) => void;
  showOwnedCards: boolean;
  setShowOwnedCards: (value: boolean) => void;
  showUnownedCards: boolean;
  setShowUnownedCards: (value: boolean) => void;
  resetFilters: () => void;
  paginatedImages: NftsWithUserOwnedType[];
  isCreateEditDeckModeEnabled: boolean;
  maxSameCardInDeck: number;
  currentPage: number;
  totalPages: number;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  createNewDeck: () => void;
  canCreateDeck?: boolean;
  deckManagementEnabled?: boolean;
  setShowAllUserDecks: (value: boolean) => void;
  numberOfCardsNeeded: number;
  maxSameTokenCardInDeck: number;
  listOfAllUserOwnedNft: UserOwnedNftCardType[];
  avgPrices?: Record<number, number>;
  incUsd?: number;
  onWithdrawalComplete: (nftId: number, amount: number) => void;
  onDepositComplete: (nftId: number, amount: number) => void;
  onCollectionStale?: () => void;
};

export const ShowNft = ({
  // deckCost,
  // setDeckCost,
  // maxDeckCost,
  createEditDeck,
  setCreateEditDeck,
  searchQuery,
  setSearchQuery,
  showAdvancedFilters,
  setShowAdvancedFilters,
  rarityFilters,
  handleRarityChange,
  typeFilters,
  handleTypeChange,
  factionFilters,
  handleFactionChange,
  setAttackFromFilter,
  setAttackToFilter,
  setDefenceFromFilter,
  setDefenceToFilter,
  showOwnedCards,
  setShowOwnedCards,
  showUnownedCards,
  setShowUnownedCards,
  resetFilters,
  paginatedImages,
  isCreateEditDeckModeEnabled,
  maxSameCardInDeck,
  currentPage,
  totalPages,
  handlePrevPage,
  handleNextPage,
  createNewDeck,
  canCreateDeck = true,
  deckManagementEnabled = true,
  setShowAllUserDecks,
  numberOfCardsNeeded,
  maxSameTokenCardInDeck,
  listOfAllUserOwnedNft,
  avgPrices,
  incUsd,
  onWithdrawalComplete,
  onDepositComplete,
  onCollectionStale
}: ShowNftProps) => {
  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)' }}>
      <Box sx={{ px: '1rem', pt: '1.5rem' }}>
        <Typography
          sx={{
            font: '800 10px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: '#ffcf40'
          }}
        >
          Digital Collectibles
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontWeight: 900,
            fontSize: 'clamp(24px, 3.4vw, 36px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#e8f4fb'
          }}
        >
          Your{' '}
          <Box component="span" sx={{ color: '#38e8ff' }}>
            cards
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            font: '600 13px/1.55 ui-monospace, Menlo, Consolas, monospace',
            color: '#8ea3b3',
            maxWidth: 640
          }}
        >
          Every card in the game — the ones you own and the ones still to
          collect. Search, filter, and browse your assets.
        </Typography>
      </Box>

      {/* Deck management (create + view/edit) is hidden while the portal deck
          builder is disabled — see deckManagementEnabled in NFTCollection. */}
      {deckManagementEnabled && (
        <Box
          sx={{
            px: '1rem',
            pt: '2rem'
          }}
        >
          <Box
            sx={{
              border: '1px solid white',
              display: 'flex',
              justifyContent: 'space-between',
              borderRadius: '8px',
              bgcolor: 'black'
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => createNewDeck()}
              disabled={!canCreateDeck}
              sx={{ fontWeight: 800, backgroundColor: '#38e8ff', color: 'white' }}
            >
              {canCreateDeck ? 'Create Deck' : 'Max 3 Decks'}
            </Button>
            <Box>
              <Box sx={{ py: '0.5rem', px: '0.5rem' }}>
                <Typography variant="h5" fontWeight="bold" textAlign="center">
                  User Decks
                </Typography>
              </Box>
              <Box sx={{ pb: '0.5rem', px: '0.5rem' }}>
                <Typography textAlign="center" fontWeight="bold">
                  Create a deck to play the TCG
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowAllUserDecks(true)}
              sx={{ fontWeight: 800, backgroundColor: '#38e8ff', color: 'white' }}
            >
              Current Decks
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ px: '1rem', pt: '2rem' }}>
        <TotalNFTCount
          listOfAllUserOwnedNft={listOfAllUserOwnedNft}
          avgPrices={avgPrices}
          incUsd={incUsd}
        />
      </Box>

      <Box
        sx={{
          maxWidth: '2000px',
          px: '1rem',
          pt: '2rem',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <NFTFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          rarityFilters={rarityFilters}
          handleRarityChange={handleRarityChange}
          typeFilters={typeFilters}
          handleTypeChange={handleTypeChange}
          factionFilters={factionFilters}
          handleFactionChange={handleFactionChange}
          setAttackFromFilter={setAttackFromFilter}
          setAttackToFilter={setAttackToFilter}
          setDefenceFromFilter={setDefenceFromFilter}
          setDefenceToFilter={setDefenceToFilter}
          showOwnedCards={showOwnedCards}
          setShowOwnedCards={setShowOwnedCards}
          showUnownedCards={showUnownedCards}
          setShowUnownedCards={setShowUnownedCards}
          resetFilters={resetFilters}
          isCreateEditDeckModeEnabled={isCreateEditDeckModeEnabled}
        />
      </Box>

      <Box
        sx={{
          maxWidth: '2000px',
          px: '1rem',
          pt: '1rem',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <SecureNFTCards
          filteredCards={paginatedImages}
          isCreateEditDeckModeEnabled={isCreateEditDeckModeEnabled}
          createEditDeck={createEditDeck}
          // deckCost={deckCost}
          // maxDeckCost={maxDeckCost}
          // setDeckCost={setDeckCost}
          maxSameCardInDeck={maxSameCardInDeck}
          setCreateEditDeck={setCreateEditDeck}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          amountOfCardsCurrentlyInDeck={0}
          numberOfCardsNeeded={numberOfCardsNeeded}
          maxSameTokenCardInDeck={maxSameTokenCardInDeck}
          onWithdrawalComplete={onWithdrawalComplete}
          onDepositComplete={onDepositComplete}
          onCollectionStale={onCollectionStale}
        />
      </Box>
    </Box>
  );
};
